import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreditView, OpsCustomer, OpsCustomerDetail } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { EmptyState, ErrorState, Kpi, LineChips, Loading, StatusPill, useTitle } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { STATE_ART } from '../../lib/voice.js';
import { count, initials, peso } from '../../lib/format.js';
import { Fig, PageHead, Ring, Stat } from '../../ui/viz';
import { Meter } from '../../catalog/kit';
import '../../catalog/catalog.css';
import { CREDIT_LIMITS, creditSummary } from '../express/lib.js';
import { DialogActions, Drawer, MoneyField, Modal, StaleNote, TextField } from '../payments/kit';
import { manilaDate, manilaStamp } from '../reports/period.js';
import { SEARCH_DEBOUNCE_MS, contactLine, customerKeys, creditView, customerParams, customerRow, customerSince, customerTitle, giveCredit, isLive, stageView } from './lib.js';

/**
 * /ops/customers: find a customer by name, email or phone; the detail sheet has their
 * addresses, latest orders and credits, and "Give a credit" (the Phase 6 goodwill endpoint).
 */
export function OpsCustomersPage() {
  useTitle('Customers');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setQuery(customerParams(q).q ?? ''), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [q]);

  const list = useInfiniteQuery({
    queryKey: customerKeys.list(query),
    queryFn: ({ pageParam }) => api.opsCustomers({ ...customerParams(query), cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const open = rows.find((r) => r.userId === openId) ?? null;

  return (
    <>
      <PageHead
        title="Customers"
        meta={list.data ? `${count(rows.length)}${list.hasNextPage ? '+' : ''} ${query ? 'matching' : 'newest first'}` : undefined}
        actions={
          <label className="search">
            <span className="sr-only">Search customers</span>
            <Icon name="search" />
            <input type="search" placeholder="Name, email or phone" value={q} maxLength={80} onChange={(e) => setQ(e.target.value)} />
          </label>
        }
      />

      {list.data && rows.length ? <CustomerStats rows={rows} more={!!list.hasNextPage} /> : null}

      <section className="card dv-card dv-rise" aria-labelledby="cu-list-h" style={{ ['--i' as string]: 1 }}>
        <div className="dv-card-h">
          <h2 id="cu-list-h">{query ? `Customers matching “${query}”` : 'All customers'}</h2>
          {list.data ? (
            <span className="dv-chip">
              {count(rows.length)}
              {list.hasNextPage ? '+' : ''}
            </span>
          ) : null}
        </div>
        {list.isError && !list.data ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Loading label="Loading customers" rows={3} />
        ) : rows.length === 0 ? (
          query ? (
            <EmptyState art={STATE_ART.noResults} title="No one by that name">
              Try part of the email, or the phone number without spaces.
            </EmptyState>
          ) : (
            <EmptyState art={STATE_ART.noOrders} title="No customers yet">
              People show up here the moment they sign in to the PopCart app.
            </EmptyState>
          )
        ) : (
          <>
            <StaleNote show={list.isError} what="the list" />
            <CustomersTable rows={rows} onOpen={setOpenId} />
            {list.hasNextPage ? (
              <div className="row cat-more">
                <button type="button" className="btn" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage} aria-busy={list.isFetchingNextPage || undefined}>
                  {list.isFetchingNextPage ? 'Loading…' : 'Show more customers'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      <CustomerDrawer summary={open} userId={openId} onClose={() => setOpenId(null)} />
    </>
  );
}

/** Figures for the loaded list: who has ordered, what they spent, credit still out. */
function CustomerStats({ rows, more }: { rows: OpsCustomer[]; more: boolean }) {
  const ordered = rows.filter((r) => r.ordersCount > 0).length;
  const orders = rows.reduce((n, r) => n + r.ordersCount, 0);
  const delivered = rows.reduce((n, r) => n + r.deliveredCount, 0);
  const spent = rows.reduce((n, r) => n + r.spentCentavos, 0);
  const credit = rows.reduce((n, r) => n + Math.max(0, r.creditBalanceCentavos), 0);
  const withCredit = rows.filter((r) => r.creditBalanceCentavos > 0).length;
  const shown = more ? 'Loaded so far' : undefined;
  return (
    <div className="dv-stats dv-rise" style={{ ['--i' as string]: 0 }}>
      <Stat label="Have ordered" value={`${ordered} / ${rows.length}`} sub={shown} visual={<Ring pct={rows.length ? (ordered / rows.length) * 100 : 0} tone="good" />} />
      <Stat label="Orders" value={count(orders)} sub={`${count(delivered)} delivered`} visual={<Ring pct={orders ? (delivered / orders) * 100 : 0} tone="good" />} />
      <Stat label="Spent" value={peso(spent)} sub={ordered ? `${peso(Math.round(spent / ordered))} per buyer` : undefined} />
      <Stat label="Credit out" value={peso(credit)} sub={withCredit ? `${withCredit} with credit` : 'None out'} />
    </div>
  );
}

function CustomersTable({ rows, onOpen }: { rows: OpsCustomer[]; onOpen(id: string): void }) {
  const top = Math.max(1, ...rows.map((c) => c.spentCentavos));
  return (
    <div className="table-wrap">
      <table className="mk-table">
        <thead>
          <tr>
            <th scope="col">Customer</th>
            <th scope="col" className="num">
              Orders
            </th>
            <th scope="col" className="num">
              Spent
            </th>
            <th scope="col" className="num">
              Credit
            </th>
            <th scope="col" className="num">
              Last order
            </th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const r = customerRow(c);
            return (
              <tr
                key={r.id}
                className="mk-click"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('a,button')) return;
                  onOpen(r.id);
                }}
              >
                <td className="ops-wrap">
                  <span className="mk-who-in">
                    <span className={r.deleted ? 'avatar mk-av off' : 'avatar mk-av'} aria-hidden="true">
                      {initials(c.name, c.email)}
                    </span>
                    <span className="stack s4" style={{ minWidth: 0 }}>
                      <span className="mk-name">{r.title}</span>
                      {r.contact ? <span className="mk-sub">{r.contact}</span> : null}
                      {r.deleted ? <StatusPill tone="neutral">Account deleted</StatusPill> : null}
                    </span>
                  </span>
                </td>
                <td className="num">
                  <span className="mk-figbar" title={r.delivered}>
                    <Fig text={r.orders} size="xs" animate={false} />
                    <span className="mk-sub">{c.ordersCount ? `${count(c.deliveredCount)} delivered` : 'none yet'}</span>
                  </span>
                </td>
                <td className="num">
                  <span className="mk-figbar">
                    <Fig text={r.spent} size="xs" animate={false} />
                    <Meter value={c.spentCentavos / top} />
                  </span>
                </td>
                <td className="num">{r.hasCredit ? <span className="dv-chip good">{r.credit}</span> : <span className="muted">—</span>}</td>
                <td className="num">
                  <span className={c.lastOrderAt ? 'mk-numtxt' : 'mk-numtxt muted'}>{r.lastOrder}</span>
                </td>
                <td className="num">
                  <div className="actions">
                    <button type="button" className="mk-go press" onClick={() => onOpen(r.id)} aria-label={`Open ${r.title}`}>
                      <Icon name="chevronRight" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CustomerDrawer({ summary, userId, onClose }: { summary: OpsCustomer | null; userId: string | null; onClose(): void }) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: customerKeys.detail(userId ?? ''), queryFn: () => api.opsCustomer(userId ?? ''), enabled: !!userId });
  const [crediting, setCrediting] = useState(false);
  useEffect(() => setCrediting(false), [userId]);
  const d = detail.data;
  const head = d ?? summary;
  const now = Date.now();

  return (
    <Drawer open={userId !== null} labelledBy="cu-drawer-title" onClose={onClose}>
      <div className="stack s24">
        <div className="row between" style={{ alignItems: 'flex-start' }}>
          <div className="stack s4">
            <h2 id="cu-drawer-title" className="t-headline">
              {head ? customerTitle(head) : 'Customer'}
            </h2>
            {head ? <span className="t-caption muted">{contactLine(head) || head.email || head.phone || ' '}</span> : null}
          </div>
          <button type="button" className="btn btn-quiet icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>

        {detail.isError && !d ? (
          <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
        ) : !d ? (
          <Loading label="Loading the customer" rows={3} />
        ) : (
          <>
            <div className="ops-kpis mk-kpis">
              <Kpi label="Orders" value={count(d.ordersCount)} detail={`${count(d.deliveredCount)} delivered`} />
              <Kpi label="Spent" value={peso(d.spentCentavos)} detail="delivered orders" />
              <Kpi label="Credit" value={peso(d.creditBalanceCentavos)} detail="usable now" />
            </div>
            <div className="row between wrap">
              <span className="t-caption muted">{customerSince(d)}</span>
              {d.deleted ? (
                <StatusPill tone="neutral">Account deleted</StatusPill>
              ) : (
                <button type="button" className="btn btn-primary" onClick={() => setCrediting(true)}>
                  <Icon name="refund" />
                  Give a credit
                </button>
              )}
            </div>

            <section className="stack s12" aria-labelledby="cu-addr-h">
              <h3 id="cu-addr-h" className="label">
                Addresses
              </h3>
              {d.addresses.length ? (
                <ul className="stack">
                  {d.addresses.map((a) => (
                    <li key={a.id} className="note stack s4">
                      <b>{a.label}</b>
                      <span className="t-caption">{a.text}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No saved addresses.</p>
              )}
            </section>

            <section className="stack s12" aria-labelledby="cu-orders-h">
              <h3 id="cu-orders-h" className="label">
                Latest orders
              </h3>
              <CustomerOrders orders={d.orders} />
            </section>

            <section className="stack s12" aria-labelledby="cu-credits-h">
              <h3 id="cu-credits-h" className="label">
                Credits
              </h3>
              {d.credits.length ? (
                <ul className="stack">
                  {d.credits.map((c) => {
                    const v = creditView(c, now);
                    return (
                      <li key={c.id} className="row between wrap note">
                        <span className="stack s4">
                          <b>
                            {peso(c.amountCentavos)} · {v.reason}
                          </b>
                          <span className="t-monocap muted">
                            ISSUED {manilaDate(c.createdAt)} · UNTIL {v.until}
                          </span>
                        </span>
                        <StatusPill tone={v.tone}>{v.state}</StatusPill>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="muted">No credits yet.</p>
              )}
            </section>
          </>
        )}
      </div>
      {d ? (
        <GiveCreditDialog
          customer={d}
          open={crediting}
          onClose={() => setCrediting(false)}
          onIssued={() => {
            void qc.invalidateQueries({ queryKey: customerKeys.detail(d.userId) });
            void qc.invalidateQueries({ queryKey: ['ops', 'customers'] });
          }}
        />
      ) : null}
    </Drawer>
  );
}

function CustomerOrders({ orders }: { orders: OpsCustomerDetail['orders'] }) {
  if (!orders.length) return <p className="muted">No orders yet.</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th scope="col">Order</th>
            <th scope="col">Status</th>
            <th scope="col" className="num">
              Total
            </th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const st = stageView(o.stage);
            return (
              <tr key={o.orderId}>
                <td>
                  <span className="t-mono">{o.code}</span>
                  <div className="t-monocap muted">{manilaStamp(o.createdAt)}</div>
                  <LineChips lines={o.lines} speed={o.speed} />
                </td>
                <td>
                  <StatusPill tone={st.tone}>{st.label}</StatusPill>
                </td>
                <td className="num">{peso(o.totalCentavos)}</td>
                <td className="num">
                  <div className="actions">
                    {isLive(o.stage) ? (
                      <Link className="btn btn-quiet" to="/ops/live" aria-label={`${o.code} on the live board`}>
                        Live board
                      </Link>
                    ) : o.stage === 'delivered' ? (
                      <Link className="btn btn-quiet" to={`/ops/refunds?order=${encodeURIComponent(o.orderId)}&code=${encodeURIComponent(o.code)}`} aria-label={`Refund ${o.code}`}>
                        Refund
                      </Link>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GiveCreditDialog({ customer, open, onClose, onIssued }: { customer: OpsCustomerDetail; open: boolean; onClose(): void; onIssued(): void }) {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ amount?: string; note?: string }>({});
  const [created, setCreated] = useState<CreditView | null>(null);
  const amountRef = useRef<HTMLInputElement>(null);
  const name = customerTitle(customer);

  useEffect(() => {
    if (!open) return;
    setAmount('');
    setNote('');
    setErrors({});
    setCreated(null);
  }, [open]);

  const issue = useMutation({
    mutationFn: (form: { amount: string; note: string }) => giveCredit(api, customer.userId, form),
    onSuccess: (r) => {
      if (!r.ok) {
        setErrors(r.errors);
        return;
      }
      setCreated(r.credit);
      toast(creditSummary(r.credit));
      onIssued();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    issue.mutate({ amount, note });
  };

  return (
    <Modal open={open} labelledBy="cu-credit-title" onClose={onClose}>
      {created ? (
        <div className="stack s24">
          <div className="stack s4">
            <h2 id="cu-credit-title" className="t-headline">
              Credit issued
            </h2>
            <span className="t-caption muted">{name} sees it in Me → Credits and at checkout.</span>
          </div>
          <div className="note stack s4" role="status">
            <span className="t-price">{peso(created.amountCentavos)}</span>
            <span className="t-label">{creditSummary(created)}</span>
          </div>
          <div className="row ops-dialog-actions">
            <button type="button" className="btn btn-primary" onClick={onClose} autoFocus>
              Done
            </button>
          </div>
        </div>
      ) : (
        <form className="stack s24" onSubmit={submit} noValidate>
          <div className="stack s4">
            <h2 id="cu-credit-title" className="t-headline">
              Give {name} a credit
            </h2>
            <span className="t-caption muted">It pays the delivery fee and Express surcharge of a later order, never goods or cash.</span>
          </div>
          <MoneyField
            id="cu-credit-amount"
            label="Amount"
            value={amount}
            onChange={setAmount}
            error={errors.amount}
            help={`${peso(CREDIT_LIMITS.minCentavos)} to ${peso(CREDIT_LIMITS.maxCentavos)}.`}
            inputRef={amountRef}
          />
          <TextField id="cu-credit-note" label="Note (kept in the audit log)" value={note} onChange={setNote} error={errors.note} multiline maxLength={CREDIT_LIMITS.noteMax} />
          {issue.isError ? (
            <p className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
              {errorMessage(issue.error, "Couldn't issue the credit. Try again.")}
            </p>
          ) : null}
          <DialogActions busy={issue.isPending} busyLabel="Issuing…" confirm="Issue credit" onCancel={onClose} />
        </form>
      )}
    </Modal>
  );
}
